import z from 'zod/v4';
import { EnumParams } from '../events.types';
import {
  leaderboardTaskItemSchema,
  rewardSchema,
  sparseRangeSchema,
} from './event-shared-object.schema';
import { userTargetingSectionSchema } from './event-sections.schemas';

const leagueDivisionConfiguration = (enums?: EnumParams) =>
  z.discriminatedUnion('type', [
    z.object({
      type: z.literal('FixedNumber'),
      amount: z.number().nonnegative(),
    }),
    z.object({
      type: z.literal('FixedSize'),
      size: z.number().nonnegative(),
    }),
    z.object({
      type: z.literal('PeriodicFixed'),
      amount: z.number().nonnegative(),
      period: enums ? z.enum(enums.ResetPeriodType) : z.string(),
      periodicDuration: z.iso.duration().optional(),
      periodicWeekDays: z
        .object({
          sunday: z.boolean(),
          monday: z.boolean(),
          tuesday: z.boolean(),
          wednesday: z.boolean(),
          thursday: z.boolean(),
          friday: z.boolean(),
          saturday: z.boolean(),
        })
        .optional(),
      startTime: z.iso.time().optional(),
    }),
    z.object({
      type: z.literal('DynamicNumber'),
      dynamicNumber: z.object({
        targetSize: z.number().nonnegative(),
        waitingPeriod: z.iso.duration(),
      }),
    }),
    z.object({
      type: z.literal('PeriodicDynamic'),
      period: enums ? z.enum(enums.ResetPeriodType) : z.string(),
      periodicDuration: z.iso.duration().optional(),
      periodicWeekDays: z
        .object({
          sunday: z.boolean(),
          monday: z.boolean(),
          tuesday: z.boolean(),
          wednesday: z.boolean(),
          thursday: z.boolean(),
          friday: z.boolean(),
          saturday: z.boolean(),
        })
        .optional(),
      periodicDynamic: z.object({
        targetSize: z.number().nonnegative(),
        minimumSize: z.number().nonnegative(),
      }),
      startTime: z.iso.time().optional(),
    }),
  ]);

export const leaderboardLeagueSchema = (enums?: EnumParams) =>
  z.object({
    maxTaskId: z.number().int().nonnegative().optional(),
    maxProgressionRewardId: z.number().int().nonnegative().optional(),
    duration: z.string().duration().optional(),
    userTargeting: userTargetingSectionSchema(enums).optional(),
    divisionConfiguration: leagueDivisionConfiguration(enums),
    progressionRewards: z.array(
      z.object({
        id: z.number().int().positive().optional(),
        pointsRequired: z.number().nonnegative(),
        rewards: z.array(rewardSchema(enums)),
      }),
    ),
    rankRewards: z.array(
      z
        .discriminatedUnion('type', [
          z.object({
            type: z.literal('Percentile'),
            percentile: z.number().nonnegative(),
          }),
          z.object({
            type: z.literal('Absolute'),
            ranks: sparseRangeSchema,
          }),
        ])
        .and(
          z.object({
            rowWidth: z.number().int().min(1).max(5).optional(),
            rewards: z.array(rewardSchema(enums)),
          }),
        ),
    ),
    tasks: z.array(leaderboardTaskItemSchema(enums)),
  });

export const eventLeaderboardSectionSchema = (enums?: EnumParams) =>
  z.object({
    type: enums ? z.enum(enums.EventLeaderboardType) : z.string(),
    pointsCurrency: enums ? z.enum(enums.EventCurrencyType) : z.string(),
    tabConfiguration: z.object({
      hasOffers: z.boolean(),
      hasGrowthFund: z.boolean(),
    }),
    excludeIfInOtherLeaderboards: z.boolean().optional().default(true),
    disableRecurrence: z.boolean().optional().default(false),
    heroShowcaseIds: (enums?.HeroShowcaseID
      ? z.array(z.enum(enums.HeroShowcaseID))
      : z.array(z.string())
    ).optional(),
    leagues: z.array(leaderboardLeagueSchema(enums)),
  });
